import React from 'react';

import {
  IKeydownEvent,
  SelectItemType,
  UseKeyboardNavigation,
  UseSelectValue,
  UseToggleDropdown,
  VERTICAL_PADDING_VALUE,
} from '../../components/Select/Select';

export const selectHooks = {
  useClickOutside(
    setOpenState: (value: React.SetStateAction<boolean>) => void,
    setSearchValue: (value: React.SetStateAction<string>) => void,
  ) {
    return () => {
      setOpenState(false);
      setSearchValue('');
    };
  },
  useToogleDropdown({
    openState,
    setOpenState,
    setHighlightedItem,
    currentSelectedItem,
  }: UseToggleDropdown) {
    return () => {
      setOpenState(!openState);

      if (openState === false) {
        setHighlightedItem(currentSelectedItem);
      }
    };
  },
  useSearchInputChange(setSearchValue: (value: React.SetStateAction<string>) => void) {
    return (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchValue(e.currentTarget.value);
    };
  },
  useSelectValue({
    setOpenState,
    setCurrentSelectedId,
    setSearchValue,
    onChange,
  }: UseSelectValue) {
    return React.useCallback(
      (item: SelectItemType) => {
        setOpenState(false);
        setCurrentSelectedId(item.id);
        setSearchValue('');
        onChange(item.id);
      },
      [onChange, setCurrentSelectedId, setOpenState, setSearchValue],
    );
  },
  useKeyboardNavigation({
    items,
    setOpenState,
    setCurrentSelectedId,
    setSearchValue,
    onChange,
    highlightedItem,
    setHighlightedItem,
    refHighlightedItem,
    refSelectList,
  }: UseKeyboardNavigation) {
    const onKeyDown = React.useCallback(
      (e: IKeydownEvent) => {
        const isDownKey = e.key === 'ArrowDown';
        const isUpKey = e.key === 'ArrowUp';
        const isEnter = e.key === 'Enter';

        if (!items.length || !highlightedItem) {
          return;
        }

        if (isEnter) {
          setOpenState(false);
          setCurrentSelectedId(highlightedItem.id);
          setSearchValue('');
          onChange(highlightedItem.id);
          return;
        }

        let currentIndex = items.indexOf(highlightedItem);

        if (
          refSelectList
          && refHighlightedItem
          && refSelectList.current
          && refHighlightedItem.current
        ) {
          const highlightedItemOffsetTop = refHighlightedItem.current.offsetTop;
          const highlightedItemHeight = refHighlightedItem.current.clientHeight;
          const highlightedItemOffsetHeight = refHighlightedItem.current.offsetHeight;
          const selectListHeight = refSelectList.current.clientHeight;
          const selectListScrollHeight = refSelectList.current.scrollHeight;

          let scrollTop = 0;

          if (isDownKey) {
            if (currentIndex === items.length - 1) {
              currentIndex = 0;
              scrollTop = 0;
            } else {
              currentIndex++;

              if (highlightedItemOffsetTop + highlightedItemHeight * 2 > selectListHeight) {
                scrollTop = highlightedItemOffsetTop
                + highlightedItemHeight - VERTICAL_PADDING_VALUE;
              }
            }
          } else if (isUpKey) {
            if (currentIndex <= 0) {
              currentIndex = items.length - 1;
              scrollTop = selectListScrollHeight;
            } else {
              currentIndex--;

              if (highlightedItemOffsetHeight <= highlightedItemHeight) {
                scrollTop = highlightedItemOffsetTop - highlightedItemHeight
                 - VERTICAL_PADDING_VALUE;
              }
            }
          } else {
            return;
          }

          refSelectList.current.scrollTop = scrollTop;
        }

        setHighlightedItem(items[currentIndex]);
        e.preventDefault();
      },
      [
        items,
        highlightedItem,
        setHighlightedItem,
        setOpenState,
        setCurrentSelectedId,
        setSearchValue,
        onChange,
        refSelectList,
        refHighlightedItem,
      ],
    );

    return { onKeyDown, refSelectList };
  },
  useFilteredItems(items: SelectItemType[], searchValue: string) {
    const filteredItems = items.filter((item: SelectItemType) => {
      return !searchValue || item.label.toLowerCase().includes(searchValue.toLowerCase());
    }, []);

    return { filteredItems };
  },
  useStates(
    defaultSelectedItemId: string,
  ) {
    const [searchValue, setSearchValue] = React.useState<string>('');
    const [openState, setOpenState] = React.useState<boolean>(false);
    const [currentSelectedId, setCurrentSelectedId] = React.useState<string>(defaultSelectedItemId);
    const [highlightedItem, setHighlightedItem] = React.useState<SelectItemType | null>(null);

    return {
      searchValue,
      setSearchValue,
      openState,
      setOpenState,
      currentSelectedId,
      setCurrentSelectedId,
      highlightedItem,
      setHighlightedItem,
    };
  },
  useRefs() {
    const refSearchInput = React.useRef<HTMLInputElement>(null);
    const refHighlightedItem = React.useRef<HTMLDivElement>(null);
    const refSelectList = React.useRef<HTMLDivElement>(null);

    return {
      refSearchInput,
      refHighlightedItem,
      refSelectList,
    };
  },
};
